import {
  ButtonBase,
  ButtonBaseProps,
  createStyles,
  Theme,
  makeStyles,
  Box,
} from "@material-ui/core";

import { Link } from "~components/Link";
import { Loader } from "~components/Loader";

type ButtonProps = {
  isLoading?: boolean;
  /** for usage with react-router Link */
  to?: string;
  buttonType?: "primary" | "secondary" | "transparent";
} & ButtonBaseProps;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    miniReset: {
      fontFamily: theme.typography.fontFamily,
      position: "relative",

      appearance: "none",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",

      padding: theme.spacing(2),

      minWidth: 152,

      fontSize: 14,
      lineHeight: "16px",
      textTransform: "uppercase",
      letterSpacing: "1.6px",
      fontWeight: "bold",

      textAlign: "center",
      textDecoration: "none",

      border: "none",
      borderRadius: 4,

      boxShadow: "0 4px 8px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.1)",

      transition: "background-color 0.18s ease-in-out",

      "&:hover": {
        outline: 0,
        border: "none",
      },

      "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.6,
      },

      "&:active": {
        boxShadow: "none",
      },

      "&[data-is-loading]": {
        cursor: "wait",
        opacity: 1,
      },
    },
    primary: {
      color: "white",
      backgroundColor: theme.palette.primary.main,

      "&::after": {
        content: "' '",
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: 4,

        opacity: 0,
        boxShadow: "inset 0 0 0 2px rgba(247, 247, 247, 0.4)",
        transition: "opacity .18s ease-in-out",
      },

      "&:hover::after": {
        opacity: 1,
      },

      "&:active": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    secondary: {
      color: theme.palette.battleshipGrey.main,
      backgroundColor: "#fff",

      "&::after": {
        content: "' '",
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: 4,

        opacity: 0,
        boxShadow: `inset 0 0 0 2px ${theme.palette.ice.main}`,
        transition: "opacity .3s ease-in-out",
      },

      "&:hover::after": {
        opacity: 1,
      },

      "&:active": {
        backgroundColor: "rgba(32, 37, 43, 0.05)",
      },
    },
    transparent: {
      color: theme.palette.battleshipGrey.main,
      backgroundColor: "transparent",

      boxShadow: "none",

      "&::after": {
        content: "' '",
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        borderRadius: 4,

        opacity: 0,
        boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12)",
        transition: "opacity .3s ease-in-out",
      },

      "&:hover::after": {
        opacity: 1,
      },

      "&:active": {
        backgroundColor: "rgba(32, 37, 43, 0.05)",
      },
    },
  }),
);

export const Button = ({ isLoading, to, buttonType = "primary", ...rest }: ButtonProps) => {
  const classes = useStyles();

  if (isLoading) {
    return (
      <ButtonBase
        className={`${classes.miniReset} ${classes[buttonType]}`}
        data-is-loading
        aria-disabled
        disabled
      >
        <Box
          position="absolute"
          top="50%"
          left="50%"
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <Loader />
        </Box>
        <Box display="flex" color="transparent">
          {rest?.children}
        </Box>
      </ButtonBase>
    );
  }

  if (to) {
    return (
      <ButtonBase
        className={`${classes.miniReset} ${classes[buttonType]}`}
        component={Link}
        to={to}
      />
    );
  }

  return <ButtonBase className={`${classes.miniReset} ${classes[buttonType]}`} {...rest} />;
};
